import React, { useState } from "react";
import {
  Camera,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import SectionHeader from "@/components/v2/SectionHeader";
import EmptyState from "@/components/v2/EmptyState";
import TableSkeleton from "@/components/v2/TableSkeleton";
import usePortfolioSnapshots from "@/components/v2/hooks/usePortfolioSnapshots";

export default function PortfolioSnapshotsTab() {
  const { snapshots, loading, error, refetch, createSnapshot } = usePortfolioSnapshots();
  const [capturing, setCapturing] = useState(false);

  const handleCapture = async () => {
    setCapturing(true);
    await createSnapshot();
    setCapturing(false);
  };

  if (error) {
    return (
      <div className="bg-surface-2 border border-border rounded-sm p-6">
        <div className="flex flex-col items-center gap-3 py-8">
          <AlertCircle className="w-8 h-8 text-loss" />
          <p className="text-sm text-muted-foreground">Failed to load portfolio snapshots</p>
          <Button size="sm" variant="outline" onClick={refetch} className="rounded-sm border-border">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 border border-border rounded-sm p-4">
      <SectionHeader
        title="Portfolio Snapshots"
        description="Point-in-time portfolio records"
        actionLabel={capturing ? "Capturing..." : "Capture Now"}
        actionIcon={Camera}
        onAction={handleCapture}
      />

      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : snapshots.length === 0 ? (
        <EmptyState text="No snapshots yet. Capture your first portfolio snapshot." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Total P&L %</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Rule Triggered</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Action Taken</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {snapshots.map((snap) => (
              <TableRow key={snap.id} className="border-border">
                <TableCell>
                  <span className="font-mono text-xs text-muted-foreground">
                    {snap.created_at || snap.date
                      ? format(new Date(snap.created_at || snap.date), "dd MMM yyyy HH:mm")
                      : "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={`font-mono text-sm font-semibold ${
                      (snap.total_pnl_percent || snap.pnl_percent || 0) >= 0
                        ? "text-profit"
                        : "text-loss"
                    }`}
                  >
                    {(snap.total_pnl_percent ?? snap.pnl_percent ?? 0) >= 0 ? "+" : ""}
                    {(snap.total_pnl_percent ?? snap.pnl_percent ?? 0).toFixed(2)}%
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {snap.rule_triggered || snap.triggered_rule || "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {snap.action_taken || snap.action || "—"}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
